/**
 * ADR-0147 — pure logic behind the shared Domain override control
 * (`components/ColumnDomainControl.tsx`): option labels and the
 * coercion-loss hint phrasing. Kept here so it is unit-testable
 * (console tests are node:test on plain TS).
 */

export type ColumnDomain = "string" | "date" | "number" | "boolean";

export const COLUMN_DOMAIN_OPTIONS: Array<{ value: ColumnDomain; label: string }> = [
  { value: "date", label: "Date" },
  { value: "number", label: "Number" },
  { value: "string", label: "String" },
  { value: "boolean", label: "Boolean" },
];

export function isColumnDomain(v: unknown): v is ColumnDomain {
  return typeof v === "string" && COLUMN_DOMAIN_OPTIONS.some((o) => o.value === v);
}

/**
 * The "Auto" option's label. With no override active, Auto shows what it
 * currently resolves to — `Auto (string)`. With an override active, the
 * cache is already re-typed so the underlying auto-resolved domain is
 * unknowable; the option reads plain `Auto`.
 */
export function autoOptionLabel(override: string | undefined, resolvedDomain: string): string {
  return isColumnDomain(override) ? "Auto" : `Auto (${resolvedDomain})`;
}

/** The select's current value: the override when valid, else "auto". */
export function domainSelectValue(override: string | undefined): string {
  return isColumnDomain(override) ? override : "auto";
}

const DOMAIN_ARTICLE: Record<ColumnDomain, string> = {
  date: "a Date",
  number: "a Number",
  boolean: "a true/false value",
  string: "text",
};

export type TypeValidationFailures = Record<
  string,
  { failedCount: number; sampleFailedValues: string[] }
>;

/**
 * ADR-0147 validate-before-convert: the toast shown when a type change is
 * rejected because some values don't fit. The column is kept as-is (the
 * control reverts). Example:
 * `«CUSTOMER_CODE» can't be a Number — e.g. "AME040", "ASS020", "AUT010". Kept as-is.`
 */
export function typeValidationMessage(
  failures: TypeValidationFailures,
  attemptedDomain: ColumnDomain
): string {
  const cols = Object.entries(failures ?? {});
  if (cols.length === 0) return "That column type couldn't be applied. Kept as-is.";
  const phrase = DOMAIN_ARTICLE[attemptedDomain] ?? "the chosen type";
  const [column, d] = cols[0];
  const samples = (d.sampleFailedValues ?? []).slice(0, 3).map((v) => `"${v}"`).join(", ");
  const tail = samples ? ` — e.g. ${samples}` : "";
  return `«${column}» can't be ${phrase}${tail}. Kept as-is.`;
}

/** Next override map after a Domain change (null = back to Auto → key removed). */
export function nextOverrideMap(
  current: Record<string, string> | null | undefined,
  columnName: string,
  domain: ColumnDomain | null
): Record<string, string> {
  const next = { ...(current ?? {}) };
  if (domain === null) delete next[columnName];
  else next[columnName] = domain;
  return next;
}
